import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Platform from '../../../../../../libs/utility/Platform';
import BasicCard from '../../../../../components/card/basiccard/BasicCard';
import TextInput from '../../../../../components/input/textinput/TextInput';
import { HEADING } from '../../../../../components/heading/constants/constants';
import Heading from '../../../../../components/heading/Heading';
import { VEC_ICON_NAME } from '../../../../../components/icons/constants';
import Icons from '../../../../../components/icons/Icons';
import { CREATE_FORMS_LOADING_STATES, CREATE_FORM_STATES_MESSAGE_MAP, PAYER_TYPE, RECORDS_TO_SHOW_PER_PAGE } from '../../constants';
import { getPayer, getPayerList } from '../../CreateFormSaga';
import { componentKey, setStepperRefreshStep, setTempFormFieldsList, setToCreateFormData } from '../../CreateFormSlice';
import { componentKey as authenticationComponentKey } from '../../../../authentication/AuthenticationSlice';
import styles from './SelectPayer.styles';
import FormEventHandlers from '../../../../../../libs/utility/formeventhandlers/FormEventHandlers';
import Button from '../../../../../components/button/Button';
import { BUTTON_TYPE } from '../../../../../components/button/constants';
import AddPayer from '../../../../components/addpayernew/AddPayer';
import Loadable from '../../../../../components/loadable/Loadable';
import { FORM_NAMES, QUERY_PARAMS } from '../../../constants';
import EditPayer from '../../../../components/addpayernew/editpayer/EditPayer';
import '../directcss/common.scss';
import { CHANGE_PAYER_LABEL_IN_FORMS } from '../../../formslisting/constants';
import Pagination from '../../../../../components/richgrid/Pagination';
import useTxSearchParams from '../../../../../../libs/txsearchparams/TxSearchParamsHook';
import Forms from '../../../../../../libs/utility/formsutility/Forms';
import General from '../../../../../../libs/utility/General';
import useFormatTIN from '../../../../../../libs/customhooks/useFormatTIN';
import { ROW_SELECTION_OPTIONS } from '../../../../../components/richgrid/constants';

export default function SelectPayer() {

    const { loadingState, payerList, toCreateFormData, stepperRefreshStep} = useSelector((state) => state[componentKey]);
    const { signedInUser } = useSelector(state => state[authenticationComponentKey]);
    
    const [searchTerm, setSearchTerm] = useState("");
    const [isAddPayerModalOpen, setIsAddPayerModalOpen] = useState(false);
    const [isEditPayerModalOpen, setIsEditPayerModalOpen] = useState(false);
    const [payerIdForEdit, setPayerIdForEdit] = useState(null);
    const [is1042SForm, setIs1042SForm] = useState(false);
    const [visiblePayersInViewPort, setVisiblePayersInViewPort] = useState([]);
    const [reorderedPayerList, setReorderedPayerList] = useState([])

    const dispatch = useDispatch();

    useEffect(() => {
        setReorderedPayerList(payerList.data)
    }, [payerList])

    useEffect(() => {
        const reorderedPayerList = General.reorderArrayByActiveElementObj(payerList.data, "PayerDetailId", toCreateFormData?.selectedPayer?.PayerDetailId)
        setReorderedPayerList(reorderedPayerList)
    }, [stepperRefreshStep, payerList])

    const [paginationState, setPaginationState] = useState({
        currentPage: 1,
        pageSize: RECORDS_TO_SHOW_PER_PAGE,
        skip: 0,
    });

    const [queryParams] = useTxSearchParams();

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const filteredPayers = useMemo(() => {
        setPaginationState({...paginationState, currentPage: 1, skip: 0});
        return reorderedPayerList.filter((payer) => {
            if (payer.LastName.toLowerCase().includes(searchTerm.toLowerCase())) {
                return payer;
            }
        });
    }, [searchTerm, reorderedPayerList]);

    const handlePayerSelection = (payer) => {
        if(Forms.isPayerSelectionAllowed(queryParams.get(QUERY_PARAMS.ALLOW_PAYER_RECIPIENT_SELECTION), toCreateFormData)){
            dispatch(getPayer(payer.PayerDetailId));
        }
    }

    const editPayerBtn = (e, payerId) => {
        e.stopPropagation();
        setPayerIdForEdit(payerId);
        setIsEditPayerModalOpen(true);
    }

    useEffect(() => {
        setIs1042SForm(toCreateFormData?.selectedForm?.FormId === FORM_NAMES.FORM1042S);
    }, [toCreateFormData?.selectedForm?.FormId]);
    
    useEffect(() => {
        const startIndex = (paginationState.currentPage - 1) * paginationState.pageSize;
        let paginatedRecords = filteredPayers.slice(startIndex, startIndex + paginationState.pageSize);
        setVisiblePayersInViewPort(paginatedRecords);
    }, [paginationState.currentPage, paginationState.pageSize, filteredPayers, toCreateFormData?.selectedPayer]);

    const handleAddPayerFinalSubmit = (newlyAddedPayer) => {
        dispatch(getPayerList({ formName: toCreateFormData?.selectedForm?.FormId, year: toCreateFormData.selectedYear }));
        if(newlyAddedPayer?.PayerDetailId) {
            handlePayerSelection(newlyAddedPayer)
        }
    }

    const handleEditPayerFinalSubmit = () => {
        dispatch(getPayerList({ formName: toCreateFormData?.selectedForm?.FormId, year: toCreateFormData.selectedYear }));
    }

    const onClickNextBtn = () => {
        dispatch(setStepperRefreshStep(1))
    }

    return <>
        <Loadable 
            currentState={loadingState.state} 
            messageMap={CREATE_FORM_STATES_MESSAGE_MAP}
            loadingStates={[CREATE_FORMS_LOADING_STATES.PAYER_PAGE_LOADING, CREATE_FORMS_LOADING_STATES.DEPENDENT_FIELD_CHANGE_LOADING, CREATE_FORMS_LOADING_STATES.SAVING_FORM_LOADING]} 
            isNewLoadable={true}
            preserveChildMount={true}
        >
            <div style={styles.mainContainer}>
                <div style={styles.titleRow}>
                    <div style={styles.headerTitle}>
                        <Heading type={HEADING.H1} >
                            {signedInUser?.FirstName}, for which {CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"} are you eFiling this form?
                        </Heading>
                    </div>
                    <div style={styles.progressIndicator}>
                    </div>
                </div>
                <div style={styles.searchAndAddPayerRow}>
                    <div style={styles.payerListActionRow}>
                        <TextInput
                            name="searchPayer"
                            type="text"
                            value={searchTerm}
                            placeHolder={`Search by ${CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"} name...`}
                            onChangeCb={handleSearchChange}
                            suffixProps={{ icon: VEC_ICON_NAME.SEARCH_ICON }}
                        />
                    </div>
                    {
                        (Forms.isPayerSelectionAllowed(queryParams.get(QUERY_PARAMS.ALLOW_PAYER_RECIPIENT_SELECTION), toCreateFormData)) ? <div style={styles.titleSubTextContainer}>
                                <span style={styles.titleSubText}>
                                    Need to add a new {CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"}?
                                </span>
                                <span>
                                    <Button
                                        type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER}
                                        onClickCb={() => setIsAddPayerModalOpen(true)}
                                        style={styles.fontWeight600}
                                    >
                                        {`Add ${CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"}`}
                                    </Button>
                                </span>
                            </div> 
                        : null
                    }
                </div>
                <div style={styles.payerListContainer}>
                    <div style={styles.payerList}>
                        {   
                            (filteredPayers.length > 0) ? 
                                visiblePayersInViewPort.map((payer, idx) => (
                                    <MemoizedBasicCard
                                        key={payer.PayerDetailId}
                                        payer={payer}
                                        editPayerBtn={editPayerBtn}
                                        handlePayerSelection={handlePayerSelection}
                                        toCreateFormData={toCreateFormData}
                                        allowPayerSelection={queryParams.get(QUERY_PARAMS.ALLOW_PAYER_RECIPIENT_SELECTION)}
                                        onClickNextBtn={onClickNextBtn}
                                    />
                                ))
                                : searchTerm.length ? <p>No {CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"} matching with the searched term</p> : null
                        }
                    </div>
                    {
                        (filteredPayers.length > 0) ? <div>
                            <Pagination
                                skip={paginationState.skip}
                                take={paginationState.pageSize}
                                total={filteredPayers.length}
                                takeOptions={ROW_SELECTION_OPTIONS}
                                onChange={(take, t) => {
                                    setPaginationState({...paginationState, skip: t, currentPage: Math.ceil((take + t) / take)})
                                }}
                                onChangeTakeCb={(newTake) => {
                                    setPaginationState({ ...paginationState, pageSize: newTake, currentPage: 1, skip: 0 })
                                }}
                            />
                        </div>
                        : null
                    }
                    
                </div>
            </div>
        </Loadable>
        {isAddPayerModalOpen && <AddPayer isPopup={true} isPopupOpen={isAddPayerModalOpen} onPopupCloseCb={() => setIsAddPayerModalOpen(false)} onFinalSubmitCb={handleAddPayerFinalSubmit} is1042SForm={is1042SForm} payerLabel={CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "Payer"} />}
        {isEditPayerModalOpen && <EditPayer payerID={payerIdForEdit} isPopup={true} isPopupOpen={isEditPayerModalOpen} onPopupCloseCb={() => setIsEditPayerModalOpen(false)} onFinalSubmitCb={ handleEditPayerFinalSubmit } payerLabel={CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "Payer"} />}
    </>
}

const MemoizedBasicCard = React.memo(({ payer = {}, editPayerBtn, toCreateFormData, handlePayerSelection, allowPayerSelection, onClickNextBtn = () => {} }) => {

    const payerCardStyles = useMemo(() => styles.payerCardCustomStyles, []); 
    const { formatTIN } = useFormatTIN()
  
    return (
        <BasicCard customStyles={payerCardStyles} isHighlighted={toCreateFormData?.selectedPayer?.PayerDetailId === payer.PayerDetailId} circularCheckboxStyles={styles.activeIconStyle}>
            <div onClick={() => handlePayerSelection(payer)} style={styles.singlePayerContainer} className={"selectPayerRecipientCardContainer"}>
                <Icons familyName="vec" iconName={(payer?.IdType == (PAYER_TYPE.INDIVIDUAL || PAYER_TYPE.SSN)) ? VEC_ICON_NAME.PAYER_TYPE_INDIVIDUAL_SMALL : VEC_ICON_NAME.PAYER_TYPE_BUSINESS_SMALL} />
                <div style={styles.payerInfo}>
                    <div style={styles.payerLableConstainer}>
                        <span style={styles.payerLabel} title={payer.LastName ? payer.LastName : ""}>{General.truncateWithEllipsis(payer.LastName, 17)}</span>
                        {
                            (!Forms.isPayerSelectionAllowed(allowPayerSelection, toCreateFormData) && (toCreateFormData?.selectedPayer?.PayerDetailId === payer.PayerDetailId)) ? <span className={"editPayerRecipientBtn"}>
                                <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} title={`Edit ${CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"}`} onClickCb={(e) => editPayerBtn(e, payer?.PayerDetailId)}>
                                    <Icons familyName={"vec"} iconName={VEC_ICON_NAME.EDIT_ICON} />
                                </Button>
                            </span>
                                : ((Forms.isPayerSelectionAllowed(allowPayerSelection, toCreateFormData)) ? <span className={"editPayerRecipientBtn"}>
                                    <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} title={`Edit ${CHANGE_PAYER_LABEL_IN_FORMS[toCreateFormData?.selectedForm?.FormId] ?? "payer"}`} onClickCb={(e) => editPayerBtn(e, payer?.PayerDetailId)}>
                                        <Icons familyName={"vec"} iconName={VEC_ICON_NAME.EDIT_ICON} />
                                    </Button>
                                </span>
                                    : null)
                        }
                    </div>
                    <span style={styles.payerIdNUmberSpan}>{formatTIN(payer?.IdType, payer?.IdNumber)}</span>
                </div>
                {toCreateFormData?.selectedPayer?.PayerDetailId === payer.PayerDetailId ?
                    <span style={styles.payerCardNextButton}>
                        <Button
                            style={styles.selectAndNextBtn}
                            onClickCb={(e) => {
                                e.stopPropagation()
                                onClickNextBtn(payer)
                            }}>
                            Next
                        </Button>
                    </span>
                    :
                    <span style={styles.payerCardNextButton} className={"selectAndNextBtn"}>
                        <Button style={styles.selectAndNextBtn} onClickCb={() => onClickNextBtn(payer)}>Select & Next</Button>
                    </span>
                }
            </div>
        </BasicCard>
    );
  });